import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { sendBugReport } from '../utils/emailService';

const BUG_CATEGORIES = [
  'Wallet Connection',
  'Petition Creation / Signing',
  'Transaction / Payment',
  'Humanitarian Aid Campaign',
  'DAO Governance',
  'User Interface / Display',
  'Performance / Loading',
  'Other',
];

const ReportBugPage = () => {
  const [status, setStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [attachmentBase64, setAttachmentBase64] = useState(null);
  const [attachmentName, setAttachmentName] = useState('');
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    category: '',
    description: '',
    steps: '',
    expected: '',
    actual: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setStatus('⚠️ Only PNG, JPG, GIF, or WebP images are accepted.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setStatus('⚠️ Screenshot must be under 5 MB.');
      return;
    }

    setStatus('');
    setAttachmentName(file.name);

    const reader = new FileReader();
    reader.onload = (ev) => {
      const base64 = ev.target.result;
      setPreview(base64);
      // Strip the data URL prefix for the email payload
      setAttachmentBase64(base64.split(',')[1]);
    };
    reader.readAsDataURL(file);
  };

  const removeAttachment = () => {
    setPreview(null);
    setAttachmentBase64(null);
    setAttachmentName('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus('');

    try {
      await sendBugReport({
        ...formData,
        attachmentBase64,
        attachmentName,
      });
      setStatus('🎉 Bug report submitted! Our team will investigate and get back to you.');
      setFormData({
        name: '',
        email: '',
        category: '',
        description: '',
        steps: '',
        expected: '',
        actual: '',
      });
      removeAttachment();
    } catch (error) {
      setStatus('💥 Failed to send report. Please try again or email bugs@petition.io directly.');
      console.error('Bug report error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass =
    'w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-primary-blue focus:ring-2 focus:ring-primary-blue/20 transition-all outline-none text-sm';

  return (
    <>
      <Helmet>
        <title>Report a Bug — app.petition.io | Petition.io</title>
        <meta
          name="description"
          content="Found a bug on the Petition.io dApp? Report it here. Help us improve the platform supporting humanitarian aid campaigns and decentralized petitions worldwide."
        />
        <meta property="og:title" content="Report a Bug — app.petition.io" />
        <link rel="canonical" href="https://petition.io/report-bug" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white py-20 pt-32">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-2xl mb-4">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 8v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-neutral-dark mb-3">
              Report a <span className="text-gradient">Bug</span>
            </h1>
            <p className="text-lg text-gray-600 max-w-xl mx-auto">
              Found an issue on{' '}
              <a
                href="https://app.petition.io"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-blue font-semibold hover:underline"
              >
                app.petition.io
              </a>
              ? Help us fix it and keep humanitarian aid campaigns running smoothly.
            </p>
          </motion.div>

          {/* Info Banner */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="bg-blue-50 border border-blue-200 rounded-2xl p-5 mb-8 flex gap-4 items-start"
          >
            <div className="flex-shrink-0 w-10 h-10 bg-primary-blue/10 rounded-lg flex items-center justify-center mt-0.5">
              <svg className="w-5 h-5 text-primary-blue" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <p className="font-semibold text-neutral-dark mb-1">Your report matters</p>
              <p className="text-sm text-gray-600">
                Petition.io powers humanitarian aid campaigns, refugee support petitions, and
                civic coordination worldwide. A bug left unfixed can prevent vulnerable
                communities from being heard. Every report helps.
              </p>
            </div>
          </motion.div>

          {/* Form */}
          <motion.form
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            onSubmit={handleSubmit}
            className="bg-white rounded-2xl shadow-card p-8 space-y-6"
          >
            {/* Name + Email */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-neutral-dark mb-2">Your Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className={inputClass}
                  placeholder="Jane Doe"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-neutral-dark mb-2">Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className={inputClass}
                  placeholder="you@example.com"
                />
              </div>
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-semibold text-neutral-dark mb-2">Bug Category</label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
                className={inputClass + ' bg-white'}
              >
                <option value="" disabled>Select a category…</option>
                {BUG_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-semibold text-neutral-dark mb-2">Bug Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="4"
                required
                className={inputClass + ' resize-none'}
                placeholder="Describe what went wrong in as much detail as possible…"
              ></textarea>
            </div>

            {/* Steps */}
            <div>
              <label className="block text-sm font-semibold text-neutral-dark mb-2">Steps to Reproduce</label>
              <textarea
                name="steps"
                value={formData.steps}
                onChange={handleChange}
                rows="3"
                required
                className={inputClass + ' resize-none'}
                placeholder="1. Go to…&#10;2. Click on…&#10;3. Observe…"
              ></textarea>
            </div>

            {/* Expected / Actual */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-neutral-dark mb-2">Expected Behavior</label>
                <textarea
                  name="expected"
                  value={formData.expected}
                  onChange={handleChange}
                  rows="3"
                  required
                  className={inputClass + ' resize-none'}
                  placeholder="What should have happened?"
                ></textarea>
              </div>
              <div>
                <label className="block text-sm font-semibold text-neutral-dark mb-2">Actual Behavior</label>
                <textarea
                  name="actual"
                  value={formData.actual}
                  onChange={handleChange}
                  rows="3"
                  required
                  className={inputClass + ' resize-none'}
                  placeholder="What actually happened?"
                ></textarea>
              </div>
            </div>

            {/* Screenshot Upload */}
            <div>
              <label className="block text-sm font-semibold text-neutral-dark mb-2">
                Screenshot{' '}
                <span className="font-normal text-gray-500">(optional — PNG, JPG, GIF, WebP · max 5 MB)</span>
              </label>

              {!preview ? (
                <label
                  htmlFor="screenshot"
                  className="flex flex-col items-center justify-center w-full h-36 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-primary-blue hover:bg-blue-50 transition-colors"
                >
                  <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="text-sm text-gray-500">Click to attach a screenshot</span>
                  <input
                    id="screenshot"
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </label>
              ) : (
                <div className="relative rounded-xl overflow-hidden border border-gray-200 shadow-sm">
                  <img
                    src={preview}
                    alt="Screenshot preview"
                    className="w-full max-h-64 object-contain bg-gray-50"
                  />
                  <button
                    type="button"
                    onClick={removeAttachment}
                    className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-7 h-7 flex items-center justify-center shadow transition-colors"
                    title="Remove screenshot"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                  <p className="text-xs text-gray-500 px-3 py-2 bg-white border-t border-gray-100 truncate">
                    {attachmentName}
                  </p>
                </div>
              )}
            </div>

            {/* Submit */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={isSubmitting}
              className="w-full px-8 py-4 bg-gradient-to-r from-red-500 to-cta-orange text-white text-lg font-bold rounded-xl shadow-lg transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSubmitting ? '⏳ Submitting…' : '🐛 Submit Bug Report'}
            </motion.button>

            {/* Status */}
            {status && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center p-4 rounded-xl bg-gray-50 border border-gray-200 font-medium text-neutral-dark"
              >
                {status}
              </motion.div>
            )}
          </motion.form>

          {/* Footer note */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-center text-sm text-gray-500 mt-6"
          >
            You can also reach us directly at{' '}
            <a href="mailto:communitysupport@petition.io" className="text-primary-blue hover:underline">
              communitysupport@petition.io
            </a>
          </motion.p>
        </div>
      </div>
    </>
  );
};

export default ReportBugPage;
